/**
 * PXP Shop Web Component
 * Self-contained article component with quantity and cart functionality
 */
(function() {
    'use strict';

    /**
     * PxpShopArticle Web Component
     */
    class PxpShopArticle extends HTMLElement {
        constructor() {
            super();
            this.quantityInput = null;
            this.addToCartBtn = null;
            this.originalQuantity = null;
        }

        connectedCallback() {
            // Get article UID from attribute
            this.articleUid = this.getAttribute('data-article-uid');
            this.listType = this.getAttribute('list-type');

            // Initialize component
            this.initElements();
            this.attachEventListeners();
            this.initCartBehavior();
        }

        /**
         * Initialize DOM element references
         */
        initElements() {
            this.quantityInput = this.querySelector('.pxp-shop__article-quantity-select') || this.querySelector('.pxp-shop__article-quantity-input');
            this.addToCartBtn = this.querySelector('.pxp-shop__article-add-to-cart');
            this.updateCartBtn = this.querySelector('.pxp-shop__article-update-cart');
        }

        /**
         * Initialize cart-specific behavior
         */
        initCartBehavior() {
            // For cart view, hide update button initially and store original quantity
            if (this.listType === 'cart' && this.updateCartBtn && this.quantityInput) {
                this.originalQuantity = parseInt(this.quantityInput.value) || 1;
                this.updateCartBtn.style.display = 'none';
            }
        }

        /**
         * Attach event listeners
         */
        attachEventListeners() {
            // Quantity input direct changes (for both cart and list views)
            if (this.quantityInput && this.quantityInput.classList.contains('pxp-shop__article-quantity-input')) {
                this.quantityInput.addEventListener('change', this.handleQuantityChange.bind(this));
                this.quantityInput.addEventListener('input', this.handleQuantityChange.bind(this));
            }

            // Add to cart button
            if (this.addToCartBtn) {
                this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
            }
        }

        /**
         * Handle direct quantity input changes
         */
        handleQuantityChange() {
            console.log('handleQuantityChange');
            this.validateQuantityInput();
            this.toggleUpdateButton();
        }

        /**
         * Toggle update button visibility for cart view
         */
        toggleUpdateButton() {
            // Only apply this behavior for cart view
            if (this.listType === 'cart' && this.updateCartBtn && this.quantityInput) {
                const currentQuantity = parseInt(this.quantityInput.value) || 1;

                // Show button if quantity changed from original, hide if same
                if (currentQuantity !== this.originalQuantity) {
                    this.updateCartBtn.style.display = '';
                } else {
                    this.updateCartBtn.style.display = 'none';
                }
            }
        }

        /**
         * Validate quantity input value
         */
        validateQuantityInput() {
            if (!this.quantityInput) return;

            let value = parseInt(this.quantityInput.value) || 1;
            const min = parseInt(this.quantityInput.min) || 1;
            const max = parseInt(this.quantityInput.max) || 999;

            if (value < min) value = min;
            if (value > max) value = max;

            this.quantityInput.value = value;
        }

        /**
         * Handle add to cart
         */
        handleAddToCart(e) {
            e.preventDefault();
            // Validate quantity before adding to cart
            this.validateQuantityInput();
            const quantity = this.quantityInput ? parseInt(this.quantityInput.value) || 1 : 1;
            this.addToCart(quantity);
        }

        /**
         * Add article to cart via AJAX
         */
        addToCart(quantity) {
            // Disable button during request
            if (this.addToCartBtn) {
                this.addToCartBtn.disabled = true;
            }

            // Build AJAX URL
            const pageId = PxpShopArticle.getPageId();
            const url = '?id=' + pageId + '&type=7777&tx_pxpshop_cart[action]=addToCart&tx_pxpshop_cart[controller]=Cart&tx_pxpshop_cart[article]=' + this.articleUid + '&tx_pxpshop_cart[quantity]=' + quantity;

            fetch(url, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    PxpShopArticle.showMessage('Artikel <a href="/shop/warenkorb">zum Warenkorb</a> hinzugefügt', 'success');

                    // Emit custom event for cart update
                    this.dispatchEvent(new CustomEvent('cart-updated', {
                        bubbles: true,
                        detail: { articleUid: this.articleUid, quantity: quantity }
                    }));

                    // Handle post-update behavior based on list type
                    if (this.listType === 'cart') {
                        // For cart view, update the original quantity and hide button
                        this.originalQuantity = parseInt(this.quantityInput.value) || 1;
                        if (this.addToCartBtn) {
                            this.addToCartBtn.style.display = 'none';
                        }
                    } else {
                        // For shop list view, reset quantity to 0 = "Bitte wählen"
                        // if (this.quantityInput) {
                        //     this.quantityInput.value = 0;
                        // }
                    }
                } else {
                    PxpShopArticle.showMessage(data.message || 'Fehler beim Hinzufügen eines Artikels zum <a href="/shop/warenkorb">Warenkorb</a>', 'error');
                }
            })
            .catch(error => {
                console.error('Error:', error);
                PxpShopArticle.showMessage('Fehler beim Hinzufügen eines Artikels zum <a href="/shop/warenkorb">Warenkorb</a>', 'error');
            })
            .finally(() => {
                // Re-enable button
                if (this.addToCartBtn) {
                    this.addToCartBtn.disabled = false;
                }
            });
        }

        /**
         * Get current page ID from body data attribute or meta tag
         */
        static getPageId() {
            // Try to get from body data attribute
            const body = document.body;
            if (body.dataset.pageId) {
                return body.dataset.pageId;
            }

            // Try to get from meta tag
            const metaTag = document.querySelector('meta[name="typo3-pid"]');
            if (metaTag) {
                return metaTag.content;
            }

            // Fallback: try to parse from URL
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('id') || '1';
        }

        /**
         * Show success/error message (static method)
         */
        static showMessage(message, type) {
            // Remove existing messages
            document.querySelectorAll('.pxp-shop__message').forEach(el => el.remove());

            // Create new message
            const messageEl = document.createElement('div');
            messageEl.className = 'pxp-shop__message pxp-shop__message--' + type;
            messageEl.innerHTML = message;

            // Insert at top of shop container
            const shopContainer = document.querySelector('main.content');
            if (shopContainer) {
                shopContainer.insertBefore(messageEl, shopContainer.firstChild);

                // Auto-remove after 5 seconds
                setTimeout(function() {
                    messageEl.remove();
                }, 5000);
            }
        }
    }

    /**
     * PxpShopFilter Web Component
     * Handles category filter with automatic redirect on selection
     */
    class PxpShopFilter extends HTMLElement {
        constructor() {
            super();
            this.formElement = null;
            this.selectElement = null;
        }

        connectedCallback() {
            this.initElements();
            this.attachEventListeners();
        }

        /**
         * Initialize DOM element references
         */
        initElements() {
            this.formElement = this.querySelector('.pxp-shop__filter-form');
            this.selectElement = this.querySelector('.pxp-shop__filter-select');
        }

        /**
         * Attach event listeners
         */
        attachEventListeners() {
            if (this.formElement) {
                this.formElement.addEventListener('submit', this.handleFormSubmit.bind(this));
            }
        }

        /**
         * Handle form submit and redirect
         */
        handleFormSubmit(e) {
            e.preventDefault();

            if (!this.selectElement) return;

            const selectedOption = this.selectElement.options[this.selectElement.selectedIndex];
            const selectedLabel = selectedOption ? selectedOption.text : '';

            if (selectedLabel) {
                // Convert to lowercase and build redirect URL
                const category = selectedLabel.toLowerCase();

                // Special case: "alle" redirects to /shop
                const redirectUrl = category === 'alle' ? '/shop' : '/shop/' + category;

                // Redirect to the category page
                window.location.href = redirectUrl + '#filterResult';
            }
        }
    }

    /**
     * Cart Manager - Handles cart count updates
     */
    class PxpShopCartManager {
        constructor() {
            this.init();
        }

        init() {
            // Listen for cart-updated events from article components
            document.addEventListener('cart-updated', () => {
                this.updateCartCount();
            });
        }

        /**
         * Update cart count in header/menu
         */
        updateCartCount() {
            const pageId = PxpShopArticle.getPageId();
            const url = '?id=' + pageId + '&type=7777&tx_pxpshop_cart[action]=getCartCount&tx_pxpshop_cart[controller]=Cart';

            fetch(url, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
            .then(response => response.json())
            .then(data => {
                // Update cart count elements
                document.querySelectorAll('.pxp-shop-cart-count').forEach(element => {
                    element.textContent = data.count || 0;

                    // Add/remove badge visibility
                    if (data.count > 0) {
                        element.classList.add('pxp-shop-cart-count--visible');
                    } else {
                        element.classList.remove('pxp-shop-cart-count--visible');
                    }
                });

                // Update cart navigation item visibility and title
                const cartNavItem = document.querySelector('li.navigation__service-item[data-type="shop-cart"]');
                if (cartNavItem) {
                    const count = data.count || 0;

                    // Update title with current count
                    cartNavItem.title = count === 1 ? '1 Artikel im Warenkorb' : count + ' Artikel im Warenkorb';

                    // Update visibility
                    if (data.count > 0) {
                        cartNavItem.classList.remove('navigation__service-item--hidden');
                    } else {
                        cartNavItem.classList.add('navigation__service-item--hidden');
                    }
                }
            })
            .catch(error => {
                console.error('Fehler bei der Aktualisierung der Warenkorbanzahl:', error);
            });
        }
    }

    // Register the web components
    customElements.define('pxpshop-article', PxpShopArticle);
    customElements.define('pxpshop-filter', PxpShopFilter);

    // Initialize cart manager when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            new PxpShopCartManager();
        });
    } else {
        new PxpShopCartManager();
    }

})();
